import React from 'react'
import { T, pct, fmtOdds, MARKET_LABEL } from './ui'

/**
 * Parlay slip — build a multi-leg card from any player detail, see the combined
 * probability (product of the per-leg model probs) and the fair American price.
 * Legs are treated as INDEPENDENT; two legs from the same game are correlated, so
 * the slip flags that rather than quietly overstating the combined number.
 */
export const legKey = l => `${l.player_id}-${l.market}`

export const probToAmerican = (p) => {
  if (!p || p <= 0 || p >= 1) return null
  const dec = 1 / p
  return dec >= 2 ? Math.round((dec - 1) * 100) : Math.round(-100 / (dec - 1))
}

export function SlipButton({ slip, onOpen }) {
  if (!slip.length) return null
  return (
    <button onClick={onOpen} style={{ position: 'fixed', bottom: 72, right: 14, zIndex: 90,
      background: T.green, color: '#04140D', border: 'none', borderRadius: 22, padding: '10px 15px',
      fontSize: 13, fontWeight: 900, cursor: 'pointer', boxShadow: `0 4px 16px ${T.green}55` }}>
      🎯 Slip · {slip.length}
    </button>
  )
}

export function SlipSheet({ slip, onRemove, onClear, onClose }) {
  const combined = slip.reduce((a, l) => a * (l.prob_over || 0), 1)
  const fair = probToAmerican(combined)
  const games = new Set(slip.map(l => l.game_id).filter(Boolean))
  const correlated = games.size < slip.length   // 2+ legs share a game

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 310, background: '#000A',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.bg, width: '100%', maxWidth: 460,
        maxHeight: '88vh', overflowY: 'auto', borderRadius: '18px 18px 0 0', border: `1px solid ${T.border}`,
        borderBottom: 'none', padding: '14px 14px 28px' }}>
        <div style={{ width: 38, height: 4, borderRadius: 3, background: T.border2, margin: '0 auto 12px' }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontWeight: 900, fontSize: 17, color: T.text }}>🎯 Parlay slip</span>
          <button onClick={onClose} style={{ background: T.card2, border: `1px solid ${T.border}`,
            color: T.muted2, borderRadius: 8, width: 30, height: 30, fontSize: 15, cursor: 'pointer' }}>✕</button>
        </div>

        {slip.map(l => (
          <div key={legKey(l)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '9px 2px', borderBottom: `1px solid ${T.border}`, gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</div>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>
                {l.team} vs {l.opponent} · {MARKET_LABEL[l.market] || l.market} {l.market === 'goals' ? 'Anytime' : `O${l.line}`}
              </div>
            </div>
            <span style={{ fontFamily: T.mono, fontWeight: 800, color: T.green, fontSize: 13 }}>{pct(l.prob_over)}</span>
            <button onClick={() => onRemove(l)} style={{ background: 'none', border: 'none', color: T.muted,
              fontSize: 15, cursor: 'pointer', flexShrink: 0 }}>✕</button>
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: T.greenDim, border: `1px solid ${T.green}55`, borderRadius: 12, padding: '12px 14px', marginTop: 14 }}>
          <div>
            <div style={{ fontSize: 9.5, fontWeight: 900, letterSpacing: '0.1em', color: T.green }}>COMBINED ({slip.length} LEGS)</div>
            <div style={{ fontSize: 11, color: T.muted2, marginTop: 2 }}>Model probability · fair price</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 20, color: T.text }}>{pct(combined)}</div>
            <div style={{ fontSize: 12, color: T.green, fontFamily: T.mono }}>{fair != null ? fmtOdds(fair) : '—'}</div>
          </div>
        </div>

        {correlated && (
          <div style={{ fontSize: 11, color: T.amber, marginTop: 10, lineHeight: 1.45 }}>
            ⚠ Two or more legs are in the same game — they're correlated, so the real combined
            probability differs from this independent estimate. Treat it as a guide.
          </div>
        )}
        <div style={{ fontSize: 10, color: T.faint, marginTop: 8, lineHeight: 1.5 }}>
          Fair price from the model's own probabilities (no book vig). Research, not advice · 21+.
        </div>
        <button onClick={onClear} style={{ width: '100%', marginTop: 14, background: T.card2,
          color: T.muted2, border: `1px solid ${T.border2}`, borderRadius: 10, padding: '10px',
          fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>Clear slip</button>
      </div>
    </div>
  )
}
